import React, { useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { Box, Button, Stack, Typography } from "@mui/material";
import AutocompleteSearchCatalog from "../../Search/AutocompleteSearchCatalog/autocomplete-search-catalog";
import { assetService, cacheService, searchService } from "../../../Core/Api/services";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import LoadSequenceTable from "../../Table/LoadSequenceTable/load-sequence-table";
import TableSkeleton from "../../Table/TableSkeleton";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { useTranslation } from "react-i18next";

export type ModalTimetoolLoadSequenceElementProps = {
  onConfirm?: (fps: number, name: string) => void;
  handleLoadFromAsset: (asset: DbAssetObjectInfos) => void;
};

export interface LoadSequenceTableRow extends DbAssetObjectInfos {
  content: string;
}

type ModalTimetoolLoadSequenceProps = CommonModalProps<ModalTimetoolLoadSequenceElementProps>;

const getSequencesRecursivly = (data: DbAssetObjectInfos) => {
  const result: LoadSequenceTableRow[] = [];

  data.childObjects?.forEach((x, i) => {
    if (x.type === ObjectType.Folder || x.type === ObjectType.Group || x.type === ObjectType.GraphSkeleton) {
      const foundSequences: LoadSequenceTableRow[] | null = getSequencesRecursivly(x);
      if (foundSequences !== null) {
        foundSequences.forEach((sequence) => {
          result.push(sequence);
        });
      }
    } else if (x.type === ObjectType.TimetoolSequence) {
      data.childObjects && result.push({ ...x, content: data.name });
    }
  });
  return result;
};

export const ModalTimetoolLoadSequence = React.forwardRef<any, ModalTimetoolLoadSequenceProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, handleLoadFromAsset } = props;
  const [sequences, setSequences] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showNoSequenceFound, setShowNoSequenceFound] = useState(false);

  useEffect(() => {
    if (cacheService.GetPlatformMode() === PlatformMode.Distribution) return;

    searchService
      .SearchBasic(
        new SearchRequestNative({
          outputLayer: ObjectLayer.Asset,
          hideTitleWithoutAsset: false,
          pattern: "*",
          types: [ObjectType.TimetoolSequence],
          statuses: [ObjectStatus.Accepted],
        })
      )
      .then((rep) => {
        rep.results && rep.results.length > 0 ? setSequences(rep.results) : setShowNoSequenceFound(true);
      });
  }, []);

  const handleClickTitle = (data: any) => {
    setIsLoading(true);
    assetService.GetGraph(data.id).then((res) => {
      setIsLoading(false);
      if (!res.graph?.childObjects) return;

      const foundSequences = getSequencesRecursivly(res.graph);
      setSequences(foundSequences);
      if (foundSequences.length === 0) setShowNoSequenceFound(true);
    });
  };

  return (
    <Modal
      footer={
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={() => closeModal()}>{t("common:close")}</Button>
        </Stack>
      }
      closeModal={() => closeModal()}
      title={t("modal:title.loadSequence")}
      reference={ref}
      width={900}
    >
      {cacheService.GetPlatformMode() === PlatformMode.Distribution && (
        <AutocompleteSearchCatalog
          layer={ObjectLayer.Title}
          withPortal={true}
          enableSearchCallback={false}
          onValueProposalClick={handleClickTitle}
          onChangeQuery={() => setShowNoSequenceFound(false)}
          sx={{ padding: "0px 24px 12px 24px", width: "100%", position: "relative" }}
        />
      )}
      {sequences.length > 0 && !isLoading && (
        <LoadSequenceTable handleLoadFromAsset={handleLoadFromAsset} rows={sequences} />
      )}
      {isLoading && (
        <Stack pt={2} height={"100px"} width={"100%"} justifyContent={"center"} alignItems={"center"}>
          <TableSkeleton />
        </Stack>
      )}
      {showNoSequenceFound && (
        <Box
          height={"100px"}
          width={"100%"}
          display={"flex"}
          justifyContent={"center"}
          alignContent={"center"}
          alignItems={"center"}
        >
          <Typography variant="h5" component="h2">
            {t("modal:body.noSequencesFound")}
          </Typography>
        </Box>
      )}
    </Modal>
  );
});
